/**
 * The day of the week of a date, three ways.
 *
 * 1. **Day count.** Reingold and Dershowitz, *Calendrical Calculations: The Ultimate Edition*
 *    (Cambridge University Press, 2018): the weekday is the fixed day number (R.D.) mod 7.
 *    This is the reference the other two are checked against.
 * 2. **Zeller's congruence** (Christian Zeller, 1882), in the form
 *    h = (q + ⌊13(m + 1)/5⌋ + K + ⌊K/4⌋ + ⌊J/4⌋ + 5J) mod 7 for the Gregorian calendar and
 *    h = (q + ⌊13(m + 1)/5⌋ + K + ⌊K/4⌋ + 5 − J) mod 7 for the Julian one, where January and
 *    February count as months 13 and 14 of the previous year, K is the year of the century,
 *    J the century, and h = 0 is Saturday.
 * 3. **Doomsday** (John Conway, 1970s): every year, the last day of February and a few
 *    memorable dates (4 April, 6 June, 8 August, 10 October, 12 December, 9 May, 11 July,
 *    5 September, 7 November) fall on the same weekday, the year's "doomsday". The
 *    doomsday is found from a century anchor and the year within the century; the date is
 *    then at most three days from the nearest one.
 *
 * `weekdayFromRd` numbers 0 = Sunday … 6 = Saturday throughout.
 */

import {
  type AdoptionRegion,
  fixedFromJulian,
  isJulianLeapYear,
} from "@/lib/tool-kit/calendars/julian";
import { mod, rdFromGregorian } from "@/lib/tool-kit/calendars/rd";
import type { CivilDate } from "@/lib/tool-kit/converters/week-number";

export type CalendarKind = "gregorian" | "julian";

export function isGregorianLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

export function isLeapYearIn(kind: CalendarKind, year: number): boolean {
  return kind === "gregorian"
    ? isGregorianLeapYear(year)
    : isJulianLeapYear(year);
}

/** Zeller's weekday number: 0 = Saturday, 1 = Sunday … 6 = Friday. */
export interface ZellerSteps {
  q: number;
  /** Month as Zeller counts it: March = 3 … February = 14. */
  m: number;
  /** Year after moving January and February to the previous year. */
  adjustedYear: number;
  /** Year of the century (0–99). */
  k: number;
  /** Century. */
  j: number;
  monthTerm: number;
  leapTerm: number;
  centuryTerm: number;
  sum: number;
  h: number;
  /** 0 = Sunday … 6 = Saturday. */
  weekday: number;
}

export function zeller(
  kind: CalendarKind,
  year: number,
  month: number,
  day: number
): ZellerSteps {
  const shifted = month <= 2;
  const m = shifted ? month + 12 : month;
  const adjustedYear = shifted ? year - 1 : year;
  const k = mod(adjustedYear, 100);
  const j = Math.floor(adjustedYear / 100);
  const monthTerm = Math.floor((13 * (m + 1)) / 5);
  const leapTerm = Math.floor(k / 4);
  const centuryTerm = kind === "gregorian" ? Math.floor(j / 4) + 5 * j : 5 - j;
  const sum = day + monthTerm + k + leapTerm + centuryTerm;
  const h = mod(sum, 7);
  return {
    q: day,
    m,
    adjustedYear,
    k,
    j,
    monthTerm,
    leapTerm,
    centuryTerm,
    sum,
    h,
    weekday: mod(h + 6, 7),
  };
}

/** The date in each month that shares the year's doomsday. */
export function doomsdayDate(kind: CalendarKind, year: number, month: number) {
  const leap = isLeapYearIn(kind, year);
  const dates = [
    leap ? 4 : 3,
    leap ? 29 : 28,
    14,
    4,
    9,
    6,
    11,
    8,
    5,
    10,
    7,
    12,
  ];
  return dates[month - 1] ?? 0;
}

export interface DoomsdaySteps {
  century: number;
  /** Year of the century. */
  yy: number;
  anchor: number;
  /** ⌊yy/12⌋, yy mod 12 and ⌊(yy mod 12)/4⌋: Conway's "12" method. */
  twelves: number;
  remainder: number;
  fours: number;
  yearShift: number;
  doomsday: number;
  doomsdayInMonth: number;
  offset: number;
  weekday: number;
}

/** The weekday of a century's doomsday: Gregorian cycle of four centuries, Julian of seven. */
export function centuryAnchor(kind: CalendarKind, century: number): number {
  return kind === "gregorian"
    ? mod(5 * mod(century, 4) + 2, 7)
    : mod(-century, 7);
}

export function doomsday(
  kind: CalendarKind,
  year: number,
  month: number,
  day: number
): DoomsdaySteps {
  const century = Math.floor(year / 100);
  const yy = year - century * 100;
  const anchor = centuryAnchor(kind, century);
  const twelves = Math.floor(yy / 12);
  const remainder = yy % 12;
  const fours = Math.floor(remainder / 4);
  const yearShift = mod(twelves + remainder + fours, 7);
  const dd = mod(anchor + yearShift, 7);
  const inMonth = doomsdayDate(kind, year, month);
  const offset = mod(day - inMonth, 7);
  return {
    century,
    yy,
    anchor,
    twelves,
    remainder,
    fours,
    yearShift,
    doomsday: dd,
    doomsdayInMonth: inMonth,
    offset,
    weekday: mod(dd + offset, 7),
  };
}

/** Day of the year (1 = 1 January) in either calendar. */
export function dayOfYear(
  kind: CalendarKind,
  year: number,
  month: number,
  day: number
): number {
  const lengths = [
    31,
    isLeapYearIn(kind, year) ? 29 : 28,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31,
  ];
  let total = day;
  for (let index = 0; index < month - 1; index++) {
    total += lengths[index] ?? 0;
  }
  return total;
}

export type DateResolution =
  | { kind: CalendarKind; rd: number }
  | { error: "nonexistent" | "skipped" | "range" };

function isRealDate(kind: CalendarKind, date: CivilDate): boolean {
  if (date.month < 1 || date.month > 12 || date.day < 1) {
    return false;
  }
  const lengths = [
    31,
    isLeapYearIn(kind, date.year) ? 29 : 28,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31,
  ];
  return date.day <= (lengths[date.month - 1] ?? 0);
}

function labelNumber(date: CivilDate): number {
  return date.year * 10_000 + date.month * 100 + date.day;
}

/**
 * Reads a date typed as a label in a chosen calendar: proleptic Gregorian, proleptic
 * Julian, or the calendar a place was using then (Julian up to its switch, Gregorian
 * after). Labels dropped at the switch (5–14 October 1582 in Spain, say) never existed
 * there and come back as "skipped".
 */
export function resolveDate(
  option: "gregorian" | "julian" | AdoptionRegion,
  date: CivilDate
): DateResolution {
  if (!Number.isInteger(date.year) || date.year < 1 || date.year > 9999) {
    return { error: "range" };
  }
  let kind: CalendarKind;
  if (option === "gregorian" || option === "julian") {
    kind = option;
  } else if (labelNumber(date) <= labelNumber(option.lastJulian)) {
    kind = "julian";
  } else if (labelNumber(date) >= labelNumber(option.firstGregorian)) {
    kind = "gregorian";
  } else {
    return { error: "skipped" };
  }
  if (!isRealDate(kind, date)) {
    return { error: "nonexistent" };
  }
  const rd =
    kind === "gregorian"
      ? rdFromGregorian(date.year, date.month, date.day)
      : fixedFromJulian(date);
  return { kind, rd };
}
